import { type SettingsConfig, SettingsType } from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { ViewIds } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { BlocksLayoutType } from 'tabview/visualizations/aq-blocks/types';
import { LayoutOrientation } from '@ardoq/yfiles';
import { ORIENTATION_TO_BLOCKS } from './consts';
import { BLOCK_DIAGRAM_LAYOUT_OPTIONS } from 'tabview/blockDiagram/consts';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import { componentStyleDropdown } from 'tabview/relationshipDiagrams/leftMenuItems';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { referenceLinesDropdown } from 'tabview/assistantShared/referenceLines';
import type { FlexibleBlockDiagramViewSettings } from './types';

const VIEW_ID = ViewIds.FLEXIBLE_BLOCK_DIAGRAM;

export const LAYOUT_TYPE_OPTIONS: [BlocksLayoutType, string, string][] = [
  ['process', 'Process', 'flexibleBlockDiagram:layoutType.process'],
  ['compact', 'Compact', 'flexibleBlockDiagram:layoutType.compact'],
  [
    'seriesparallel',
    'Series-parallel',
    'flexibleBlockDiagram:layoutType.seriesparallel',
  ],
  ['grid', 'Grid', 'flexibleBlockDiagram:layoutType.grid'],
];

const activeLayoutTypeLabel = (
  activeLayoutType: BlocksLayoutType,
  i18nLocale: I18nLocale
): string => {
  const activeOption = LAYOUT_TYPE_OPTIONS.find(
    ([layoutType]) => layoutType === activeLayoutType
  );
  // Fall back to the neutral control name rather than a specific (wrong) layout
  // if the active type isn't one of the FBD options.
  if (!activeOption) {
    return t(
      i18nLocale,
      'flexibleBlockDiagram:layoutType.label',
      'Layout type'
    );
  }
  const [, label, i18nKey] = activeOption;
  return t(i18nLocale, i18nKey, label);
};

export const layoutTypeDropdown = (
  viewSettings: FlexibleBlockDiagramViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => {
  // Resolved once, mirroring `toLayoutStyle`, so the toolbar cannot name a different
  // layout than the diagram draws. `sanitizeSingleViewSettings` fills an absent key from
  // the defaults, so the fallback is defence rather than a state the pipeline delivers.
  const activeLayoutType = viewSettings.layoutType ?? 'process';

  return {
    id: 'layoutType',
    type: SettingsType.DROPDOWN,
    iconName: IconName.SITEMAP,
    label: t(
      i18nLocale,
      'flexibleBlockDiagram:layoutType.label',
      'Layout type'
    ),
    buttonText: activeLayoutTypeLabel(activeLayoutType, i18nLocale),
    options: LAYOUT_TYPE_OPTIONS.map(([layoutType, label, i18nKey]) => ({
      name: `layout_type_${layoutType}`,
      label: t(i18nLocale, i18nKey, label),
      type: DropdownOptionType.OPTION,
      isActive: activeLayoutType === layoutType,
      onClick: () => handleStoreSetting(VIEW_ID, { layoutType }),
    })),
    dropdownSize: DropdownSize.S,
  };
};

const layoutOrientationDropdown = (
  viewSettings: FlexibleBlockDiagramViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => {
  // Resolved against the table the diagram lays out from, which is total over the enum: a
  // viewpoint persisted before ARD-33247 can carry an orientation that is not a member,
  // and the diagram lays that out left to right. A member this menu does not offer stays
  // itself and ticks nothing, since there is no option for it.
  const activeOrientation =
    viewSettings.layoutOrientation in ORIENTATION_TO_BLOCKS
      ? viewSettings.layoutOrientation
      : LayoutOrientation.LEFT_TO_RIGHT;

  return {
    id: 'layoutDirection',
    type: SettingsType.DROPDOWN,
    iconName: IconName.SORT_ASC,
    label: t(
      i18nLocale,
      'appLayout:viewPaneHeader.layoutDirection',
      'Layout direction'
    ),
    options: BLOCK_DIAGRAM_LAYOUT_OPTIONS.map(
      ([label, direction, i18nKey]) => ({
        name: `layout_orientation_${direction}`,
        label: t(i18nLocale, i18nKey, label),
        type: DropdownOptionType.OPTION,
        isActive: activeOrientation === direction,
        onClick: () =>
          handleStoreSetting(VIEW_ID, { layoutOrientation: direction }),
      })
    ),
    dropdownSize: DropdownSize.S,
  };
};

type GetViewModifiersArgs = {
  viewSettings: FlexibleBlockDiagramViewSettings;
  i18nLocale: I18nLocale;
};

export const getFlexibleBlockDiagramViewModifiers = ({
  viewSettings,
  i18nLocale,
}: GetViewModifiersArgs): SettingsConfig[] => [
  layoutTypeDropdown(viewSettings, i18nLocale),
  layoutOrientationDropdown(viewSettings, i18nLocale),
  referenceLinesDropdown(VIEW_ID, viewSettings, i18nLocale),
  componentStyleDropdown(
    VIEW_ID,
    viewSettings,
    onViewSettingsUpdate,
    i18nLocale
  ),
];
